import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import deDE from "./locales/de-DE.json";
import enUS from "./locales/en-US.json";
import esMX from "./locales/es-MX.json";
import frFR from "./locales/fr-FR.json";

export const supportedLocales = [
  "es-MX",
  "en-US",
  "de-DE",
  "fr-FR",
] as const;
export type SupportedLocale = (typeof supportedLocales)[number];

/** localStorage key for an explicit language override (absent = use browser). */
export const LANGUAGE_PREFERENCE_KEY = "dinlabel.languagePreference";

export type LanguagePreference = "browser" | SupportedLocale;

function isSupportedLocale(value: string): value is SupportedLocale {
  return (supportedLocales as readonly string[]).includes(value);
}

export function normalizeLanguage(lng: string): SupportedLocale {
  const lower = lng.toLowerCase();
  if (lower.startsWith("en")) {
    return "en-US";
  }
  if (lower.startsWith("es")) {
    return "es-MX";
  }
  if (lower.startsWith("de")) {
    return "de-DE";
  }
  if (lower.startsWith("fr")) {
    return "fr-FR";
  }
  return "en-US";
}

export function getLanguagePreference(): LanguagePreference {
  if (typeof localStorage === "undefined") {
    return "browser";
  }
  const stored = localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
  if (stored && isSupportedLocale(stored)) {
    return stored;
  }
  return "browser";
}

export async function setLanguagePreference(
  preference: LanguagePreference,
): Promise<void> {
  if (typeof localStorage !== "undefined") {
    if (preference === "browser") {
      localStorage.removeItem(LANGUAGE_PREFERENCE_KEY);
    } else {
      localStorage.setItem(LANGUAGE_PREFERENCE_KEY, preference);
    }
  }

  const nextLanguage =
    preference === "browser"
      ? normalizeLanguage(
          typeof navigator !== "undefined" ? navigator.language : "en-US",
        )
      : preference;

  await i18n.changeLanguage(nextLanguage);
}

function syncDocumentLang(lng: string) {
  if (typeof document === "undefined") {
    return;
  }
  const resolved = normalizeLanguage(lng);
  document.documentElement.lang = resolved;
  // Page titles are owned by RouteSeo / applyPageSeo.
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    showSupportNotice: false,
    resources: {
      "es-MX": { translation: esMX },
      "en-US": { translation: enUS },
      "de-DE": { translation: deDE },
      "fr-FR": { translation: frFR },
    },
    fallbackLng: "en-US",
    supportedLngs: [...supportedLocales],
    preload: [...supportedLocales],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // Explicit preference first; otherwise follow the browser language.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANGUAGE_PREFERENCE_KEY,
      // Preference is written only via setLanguagePreference, so browser
      // detection does not sticky-cache a resolved locale.
      caches: [],
      convertDetectedLanguage: normalizeLanguage,
    },
  })
  .then(() => {
    syncDocumentLang(i18n.language);
  });

i18n.on("languageChanged", syncDocumentLang);

export default i18n;
